import type { Message } from '../types';
import type { EngineeringContext } from '../types';
import type { StageStatus } from './displayStrategy';
import { isCodePipelineMessage } from './displayStrategy';
import { getWorkflowStageGates } from './workflowStageGuards';
import { extractSummaryTldr, compactProjectTitle } from './messageDisplay';
import { getEffectiveValidationReport, getRepairDisplayStatus, hasFinalRepairValidation } from './repairDisplayState';
import { formatValidationRootCauseCount } from './validationRootCauseSummary';
import { deliveryValidationLabel, readDeliveryReview } from './deliveryValidationLabel';
import {
  getActiveRepairVersion,
  getRepairCodeVersions,
  getRepairVersionLabel,
} from './messageTopic';
import { normalizeCodeForDisplay } from './codeDisplay';

export type WorkspaceTab =
  | 'overview' | 'requirement' | 'solution' | 'artifacts' | 'verification';

export interface WorkspaceArtifact {
  id: string;
  kind: 'requirement' | 'plan' | 'code' | 'validation' | 'file';
  title: string;
  status: StageStatus;
  version?: number;
  codeVersionId?: string;
  codeVersionLabel?: string;
  isActive?: boolean;
  sourceMessageId?: string;
  createdAt?: number;
  /** Where the record came from. Used to reconcile registry rows with chat deliverables. */
  origin?: 'registry' | 'conversation' | 'deliverable' | 'ledger';
  fileName?: string;
  content?: string;
  previewTarget?: 'code' | 'markdown' | 'table' | 'pdf';
  downloadable?: boolean;
}

export interface WorkspaceViewModel {
  projectId?: string;
  sessionId: string;
  activeArtifactId?: string;
  activeSourceMessageId?: string;
  context: EngineeringContext | null;
  artifacts: WorkspaceArtifact[];
  activeTab: WorkspaceTab;
}

export const WORKSPACE_TABS: { id: WorkspaceTab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'requirement', label: 'Requirement' },
  { id: 'solution', label: 'Solution' },
  { id: 'artifacts', label: 'Engineering Artifacts' },
  { id: 'verification', label: 'Verification' },
];

export interface VisibleTabsInput {
  hasCode: boolean;
  hasValidation: boolean;
  hasPlan: boolean;
  hasRequirement: boolean;
  hasMarkdown: boolean;
  validationFailed: boolean;
  planReviewPending: boolean;
  requirementConfirmationActive: boolean;
}

/**
 * The top-level information architecture is deliberately stable. Pipeline
 * state changes badges and empty states, never the location of information.
 */
export function getVisibleTabs(_input: VisibleTabsInput): WorkspaceTab[] {
  return WORKSPACE_TABS.map(tab => tab.id);
}

export function getWorkspaceTabForStage(kind: string): WorkspaceTab {
  switch (kind) {
    case 'requirementThinking':
    case 'requirementAnalysis':
    case 'requirementConfirmation':
    case 'humanConfirmation':
      return 'requirement';
    case 'engineeringReasoning':
    case 'planGeneration':
    case 'planReview':
      return 'solution';
    case 'validation':
      return 'verification';
    case 'code':
    case 'repair':
      return 'artifacts';
    default:
      return 'overview';
  }
}

function hasText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function compactFirst(candidates: Array<unknown>): string {
  for (const candidate of candidates) {
    if (hasText(candidate)) {
      const compacted = compactProjectTitle(candidate);
      if (compacted) return compacted;
    }
  }
  return '';
}

function getLastHistoryItem<T>(items: T[] | undefined, predicate: (item: T) => boolean): T | undefined {
  if (!items?.length) return undefined;
  for (let index = items.length - 1; index >= 0; index -= 1) {
    if (predicate(items[index])) return items[index];
  }
  return undefined;
}

/**
 * Plan Review can be stored either on message.planReview or inside the streamed
 * planProcessHistory. The message body already uses the history-aware value, so
 * the workspace must do the same or it will point to stale / empty plan content.
 */
export function getWorkspaceMessagePlanReview(msg: Message): Message['planReview'] {
  const historyItem = getLastHistoryItem(msg.planProcessHistory, item => Boolean(item?.planReview));
  return historyItem?.planReview || msg.planReview;
}

export function getWorkspaceMessagePlanSummary(msg: Message): Message['planProcessSummary'] {
  const historyItem = getLastHistoryItem(msg.planProcessHistory, item => Boolean(item?.summary));
  return msg.planProcessSummary || historyItem?.summary;
}

function hasRawPlanGenerationArtifact(msg: Message): boolean {
  return Boolean(
    hasText(msg.planGenerationReasoning)
      // The plan stage's own text says it as well, and is all there is of the plan's prose where the gateway leaves
      // the older fields out (streamedTurnWithoutReasoning.test.ts).
      || hasText(msg.stageBuffers?.planGeneration?.text)
      || hasText(msg.planTransitionText)
      || getWorkspaceMessagePlanSummary(msg)
      || getWorkspaceMessagePlanReview(msg)
      || msg.planProcessHistory?.some(item => item.stage === 'planning' && (hasText(item.reasoning) || Boolean(item.summary) || Boolean(item.planReview))),
  );
}

function hasHumanConfirmationRequirement(msg: Message): boolean {
  return getWorkflowStageGates(msg).hasHumanConfirmation;
}
function hasPendingHumanConfirmation(msg: Message): boolean {
  return getWorkflowStageGates(msg).hasPendingHumanConfirmation;
}
function isHumanConfirmationResolved(msg: Message): boolean {
  return getWorkflowStageGates(msg).humanConfirmationResolved;
}
function isPlanApprovalResolvedOrNotRequired(msg: Message): boolean {
  return getWorkflowStageGates(msg).planApprovalResolvedOrNotRequired;
}
function canShowDownstreamArtifact(msg: Message): boolean {
  return isHumanConfirmationResolved(msg);
}

function canShowCodeAndValidationArtifact(msg: Message): boolean {
  return canShowDownstreamArtifact(msg) && isPlanApprovalResolvedOrNotRequired(msg);
}

function getCompletedRepairFinalCodeForWorkspace(msg: Message): string {
  const appendix = msg.repairAppendix;
  const repairStatus = getRepairDisplayStatus(msg);
  // Keep this aligned with MessageBubble/Timeline: a final post-repair validation
  // that passes is the latest truth. Use finalCode when the repair is terminally
  // completed or completed with warnings; keep failed/skipped candidates out of
  // the main code artifact.
  if (
    hasText(appendix?.finalCode)
      && (repairStatus === 'completed' || repairStatus === 'completed_with_warnings')
  ) {
    return appendix!.finalCode;
  }
  return '';
}

/** The exact code artifact that the workspace should represent. */
export function getWorkspaceMessageCode(msg: Message, codeVersionId?: string): string {
  if (!canShowCodeAndValidationArtifact(msg)) return '';
  if (codeVersionId) {
    const selectedCode = getRepairCodeVersions(msg.repairAppendix, msg.parsed?.code)
      .find(version => version.id === codeVersionId)?.code || '';
    return normalizeCodeForDisplay(selectedCode);
  }
  const code = getActiveRepairVersion(msg)?.code
    || getCompletedRepairFinalCodeForWorkspace(msg)
    || msg.parsed?.code
    || '';
  return normalizeCodeForDisplay(code);
}

export function isWorkspaceMessageCodeRepaired(msg: Message): boolean {
  return getActiveRepairVersion(msg)?.role === 'repair'
    || Boolean(getCompletedRepairFinalCodeForWorkspace(msg));
}

function getWorkspaceCodeVersions(msg: Message): NonNullable<NonNullable<Message['repairAppendix']>['codeVersions']> {
  if (!canShowCodeAndValidationArtifact(msg)) return [];
  return getRepairCodeVersions(msg.repairAppendix, msg.parsed?.code);
}

export function getWorkspaceValidationReport(msg: Message): any {
  if (!canShowCodeAndValidationArtifact(msg)) return null;
  return getEffectiveValidationReport(msg);
}

function hasRequirementAnalysisArtifact(msg: Message): boolean {
  return Boolean(
    msg.pipeline?.requirement
      || hasText(msg.planAnalysisReasoning)
      || msg.planProcessHistory?.some(item => item.stage === 'requirement_analysis' && hasText(item.reasoning))
      || msg.parsed?.clarify
      || msg.clarificationChecklist?.length
      || msg.pendingChipIds?.length,
  );
}

function hasPlanGenerationArtifact(msg: Message): boolean {
  return canShowDownstreamArtifact(msg) && hasRawPlanGenerationArtifact(msg);
}

function deriveRequirementTitle(msg: Message): string {
  const req = msg.pipeline?.requirement;
  const title = compactFirst([
    req?.normalizedPrompt,
    req?.rawPrompt,
    req?.desiredOutputs?.[0],
    req?.entities?.[0]?.name,
    req?.controlStrategy,
    req?.taskType,
    msg.parsed?.clarify,
  ]);
  return title || 'Requirement';
}

function deriveRequirementStatus(msg: Message): StageStatus {
  const phase = msg.progressState?.phase;
  const planPhase = msg.progressState?.planPhase;

  if (
    msg.isStreaming
      && (
        phase === 'classifying'
          || phase === 'clarifying'
          || (phase === 'planning' && (!planPhase || planPhase === 'analyzing' || planPhase === 'classifying'))
      )
  ) {
    return 'running';
  }

  if (hasPendingHumanConfirmation(msg)) return 'needs_input';
  if (hasHumanConfirmationRequirement(msg) && isHumanConfirmationResolved(msg)) return 'passed';
  if (hasPlanGenerationArtifact(msg) || getWorkspaceMessageCode(msg)) return 'passed';
  return 'passed';
}

/** Derive a meaningful code title from the parsed result or message content. */
function deriveCodeTitle(msg: Message): string {
  const reqTitle = deriveRequirementTitle(msg);
  if (reqTitle && reqTitle !== 'Requirement') return reqTitle;

  // Prefer the TL;DR program name extracted from the summary
  if (msg.parsed?.summary) {
    const tldr = extractSummaryTldr(msg.parsed.summary);
    if (tldr) return tldr;
  }
  // Fall back to the first heading or line of content
  if (msg.content) {
    const compacted = compactProjectTitle(msg.content);
    if (compacted) return compacted;
  }
  return 'PLC Code';
}

/** Derive a meaningful plan title from the plan review metadata. */
function derivePlanTitle(msg: Message): string {
  const planReview = getWorkspaceMessagePlanReview(msg);
  const planSummary = getWorkspaceMessagePlanSummary(msg);

  const title = compactFirst([
    planReview?.requirementSummary,
    planSummary?.sourceText,
    msg.planTransitionText,
    msg.pipeline?.requirement?.normalizedPrompt,
  ]);
  return title || 'Plan';
}

function derivePlanStatus(msg: Message): StageStatus {
  const planReview = getWorkspaceMessagePlanReview(msg);

  if (!isHumanConfirmationResolved(msg)) return 'pending';
  if (planReview?.reviewResult === 'approved') return 'passed';
  if (planReview?.reviewResult === 'rejected' || planReview?.reviewResult === 'regenerate_subtask') return 'failed';
  if (planReview && planReview.requiresApproval !== false) return 'needs_approval';
  if (planReview && planReview.requiresApproval === false) return 'passed';

  if (msg.isStreaming && (msg.progressState?.phase === 'planning' || msg.progressState?.stage === 'planning')) {
    return 'running';
  }

  return 'passed';
}

/** Derive a meaningful validation title from the validation report. */
function deriveValidationTitle(msg: Message): string {
  const report = getWorkspaceValidationReport(msg);
  if (report?.valid && readDeliveryReview(msg.generationMetrics?.reviewRequired)?.required) return deliveryValidationLabel('', msg.generationMetrics?.reviewRequired);
  const rootCauseLabel = report ? formatValidationRootCauseCount(report) : '';
  if (rootCauseLabel && rootCauseLabel !== '校验通过') {
    return hasFinalRepairValidation(msg)
      ? `Final Validation · ${rootCauseLabel}`
      : `Validation · ${rootCauseLabel}`;
  }
  if (report?.summary) {
    const compacted = compactProjectTitle(report.summary);
    if (compacted) return compacted;
  }
  return hasFinalRepairValidation(msg)
    ? 'Final Validation'
    : 'Validation Report';
}

function deriveValidationStatus(msg: Message): StageStatus {
  const report = getWorkspaceValidationReport(msg);
  if (!report) return 'pending';
  return report.valid !== false ? 'passed' : 'failed';
}

function mergeWorkspaceArtifacts(
  registryArtifacts: WorkspaceArtifact[],
  derivedArtifacts: WorkspaceArtifact[],
): WorkspaceArtifact[] {
  const merged: WorkspaceArtifact[] = registryArtifacts.map(artifact => ({ ...artifact, origin: artifact.origin || 'registry' as const }));

  for (const derived of derivedArtifacts) {
    const exactIndex = merged.findIndex(artifact => artifact.id === derived.id);
    if (exactIndex >= 0) {
      merged[exactIndex] = {
        ...derived,
        ...merged[exactIndex],
        sourceMessageId: merged[exactIndex].sourceMessageId || derived.sourceMessageId,
        content: merged[exactIndex].content || derived.content,
        fileName: merged[exactIndex].fileName || derived.fileName,
      };
      continue;
    }

    // Requirement, plan, code and validation rows are versioned records. When
    // both projections describe the same version, retain the registry identity
    // while filling in message provenance/content needed for preview and links.
    if (derived.kind !== 'file') {
      const compatibleIndex = merged.findIndex(artifact => (
        artifact.kind === derived.kind
        && artifact.version === derived.version
        && (!artifact.sourceMessageId || artifact.sourceMessageId === derived.sourceMessageId)
      ));
      if (compatibleIndex >= 0) {
        merged[compatibleIndex] = {
          ...derived,
          ...merged[compatibleIndex],
          sourceMessageId: merged[compatibleIndex].sourceMessageId || derived.sourceMessageId,
          content: merged[compatibleIndex].content || derived.content,
        };
        continue;
      }
    }

    const duplicateFile = derived.kind === 'file' && merged.some(artifact => (
      artifact.kind === 'file'
      && (artifact.fileName || artifact.title).toLocaleLowerCase() === (derived.fileName || derived.title).toLocaleLowerCase()
      && artifact.sourceMessageId === derived.sourceMessageId
    ));
    if (!duplicateFile) merged.push(derived);
  }

  return merged.sort((left, right) => (left.createdAt || 0) - (right.createdAt || 0));
}

export function buildWorkspaceViewModel(params: {
  sessionId: string;
  projectId?: string;
  messages: Message[];
  context: EngineeringContext | null;
  activeArtifactId?: string;
  activeSourceMessageId?: string;
  activeTab?: WorkspaceTab;
  /**
   * Artifacts from the backend registry, already adapted to WorkspaceArtifact.
   * When present these replace the message-derived list — the registry is the
   * authoritative record (real version numbers, parent chains, validation
   * status) and must not coexist with a second derived copy. Absent for
   * project-less sessions, which have no registry rows.
   */
  registryArtifacts?: WorkspaceArtifact[];
  /** Files shown by the completed conversation turn (same source as its file chip). */
  deliverableArtifacts?: WorkspaceArtifact[];
}): WorkspaceViewModel {
  const artifacts: WorkspaceArtifact[] = [];

  // Track version counters per artifact kind within this session
  const versionCounters: Record<string, number> = {};

  // Scan messages for artifacts. The extraction rules intentionally mirror
  // MessageBubble/StageTimeline so the workspace never shows a different source
  // than the actual generated content in the conversation body.
  for (const msg of params.messages) {
    if (msg.role !== 'assistant') continue;

    const isCodePipeline = isCodePipelineMessage(msg);

    // Requirement artifact — only code-generation turns should create workflow artifacts.
    if (isCodePipeline && hasRequirementAnalysisArtifact(msg)) {
      versionCounters['requirement'] = (versionCounters['requirement'] || 0) + 1;
      artifacts.push({
        id: `${msg.id}-requirement`,
        kind: 'requirement',
        title: deriveRequirementTitle(msg),
        status: deriveRequirementStatus(msg),
        version: versionCounters['requirement'],
        sourceMessageId: msg.id,
        createdAt: msg.timestamp,
        origin: 'conversation',
      });
    }

    // Plan artifact — supports both direct planReview and planProcessHistory.
    if (isCodePipeline && hasPlanGenerationArtifact(msg)) {
      versionCounters['plan'] = (versionCounters['plan'] || 0) + 1;
      artifacts.push({
        id: `${msg.id}-plan`,
        kind: 'plan',
        title: derivePlanTitle(msg),
        status: derivePlanStatus(msg),
        version: versionCounters['plan'],
        sourceMessageId: msg.id,
        createdAt: msg.timestamp,
        origin: 'conversation',
      });
    }

    // Code artifact — use repaired finalCode first, then parsed.code.
    if (isCodePipeline) {
      const codeVersions = getWorkspaceCodeVersions(msg);
      const activeVersion = getActiveRepairVersion(msg);
      for (const codeVersion of codeVersions) {
        if (!hasText(codeVersion.code)) continue;
        versionCounters['code'] = (versionCounters['code'] || 0) + 1;
        const codeVersionLabel = codeVersion.label || getRepairVersionLabel(codeVersion.id);
        artifacts.push({
          id: `${msg.id}-code-${codeVersion.id}`,
          kind: 'code',
          title: `${deriveCodeTitle(msg)} ${codeVersionLabel}`,
          status: codeVersion.role === 'repair'
            ? (codeVersion.applied ? 'repaired' : 'failed')
            : 'passed',
          version: versionCounters['code'],
          codeVersionId: codeVersion.id,
          codeVersionLabel,
          isActive: activeVersion?.id === codeVersion.id,
          sourceMessageId: msg.id,
          createdAt: codeVersion.createdAt || msg.timestamp,
          origin: 'conversation',
        });
      }
    }

    // Validation artifact — final post-repair validation wins over initial validation.
    if (isCodePipeline && getWorkspaceValidationReport(msg)) {
      versionCounters['validation'] = (versionCounters['validation'] || 0) + 1;
      artifacts.push({
        id: `${msg.id}-validation`,
        kind: 'validation',
        title: deriveValidationTitle(msg),
        status: deriveValidationStatus(msg),
        version: versionCounters['validation'],
        sourceMessageId: msg.id,
        createdAt: msg.timestamp,
        origin: 'conversation',
      });
    }
  }

  // Files from context — use ledger version. Keep these in the Files tab only;
  // generated message artifacts above remain the source of truth for tabs.
  if (params.context?.artifactLedger) {
    const seenFileIds = new Set<string>();
    for (const a of params.context.artifactLedger) {
      if (!a.artifactId || seenFileIds.has(a.artifactId)) continue;
      seenFileIds.add(a.artifactId);
      artifacts.push({
        id: a.artifactId,
        kind: 'file',
        title: a.title,
        status: a.validation?.valid !== false ? 'passed' : 'failed',
        version: a.version,
        createdAt: a.updatedAt,
        origin: 'ledger',
      });
    }
  }

  const reconciledArtifacts = mergeWorkspaceArtifacts(
    params.registryArtifacts || [],
    [...artifacts, ...(params.deliverableArtifacts || [])],
  );

  return {
    projectId: params.projectId,
    sessionId: params.sessionId,
    activeArtifactId: params.activeArtifactId,
    activeSourceMessageId: params.activeSourceMessageId,
    context: params.context,
    artifacts: reconciledArtifacts,
    activeTab: params.activeTab || 'overview',
  };
}
